import { Dtos } from "@inrev/common";
import { useFetchAccountPreviews } from "@portal/api/previews";
import {
	TrendMetricCard,
	type TrendMetricCardProps,
} from "@portal/components/patterns/metric-card";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useMemo } from "react";
import type { EntityScope } from "../../../shared/scope";
import { computeSubmissionMetrics } from "./computeSubmissionMetrics";

const EMPTY_ACCOUNT_PREVIEWS: Dtos.SuretyAccount.Preview.Get.Response[] = [];

type SubmissionMetricsViewProps = {
	scope: EntityScope;
};

export const SubmissionMetricsView = ({ scope }: SubmissionMetricsViewProps) => {
	const { user } = useAuthenticatedUser();
	const { accountPreviews } = useFetchAccountPreviews();

	const metrics = useMemo(
		() =>
			computeSubmissionMetrics({
				accountPreviews: accountPreviews ?? EMPTY_ACCOUNT_PREVIEWS,
				scope,
				currentUserId: user.id,
			}),
		[accountPreviews, scope, user.id],
	);

	const cards: TrendMetricCardProps[] = [
		{
			testId: "metric-open-submissions",
			label: "Open",
			value: metrics.open,
			tone: "accent",
			series: metrics.series.open,
		},
		{
			testId: "metric-in-review-submissions",
			label: "In Review",
			value: metrics.inReview,
			tone: "accent",
			series: metrics.series.inReview,
		},
		{
			testId: "metric-approved-submissions",
			label: "Approved",
			value: metrics.approved,
			tone: "accent",
			series: metrics.series.approved,
		},
		{
			testId: "metric-ttm-submissions",
			label: "TTM Submissions",
			// `value` is the displayed node; `current`/`prior` drive the delta pill via deriveTrend.
			value: metrics.ttmSubmissions.current,
			current: metrics.ttmSubmissions.current,
			prior: metrics.ttmSubmissions.prior,
			deltaFormat: "count",
			tone: "accent",
			series: metrics.series.ttmSubmissions,
		},
		{
			testId: "metric-ttm-activated",
			label: "TTM Converted to Active",
			value: metrics.ttmActivated.current,
			current: metrics.ttmActivated.current,
			prior: metrics.ttmActivated.prior,
			deltaFormat: "count",
			tone: "accent",
			series: metrics.series.ttmActivated,
		},
	];

	return (
		<div className="grid max-w-8xl grid-cols-2 gap-4 lg:grid-cols-5">
			{cards.map((card) => (
				<TrendMetricCard key={card.testId} {...card} />
			))}
		</div>
	);
};
